import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LocalizedLink from './LocalizedLink';
import { useLocalizedPath } from '../hooks/useLocalizedPath';
import { ArrowLeftRight, ArrowLeft } from 'lucide-react';
import SEOHead from './SEOHead';
import { powerUnits, getUnitById, convertPower, generateSEODescription, popularPowerConversions } from '../data/powerRoutes';

const PowerConversionPage = () => {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLocalizedPath();
  const { conversion } = useParams();
  const [fromValue, setFromValue] = useState('1');
  const [toValue, setToValue] = useState('');

  const parseConversion = (slug) => {
    if (!slug) return { fromId: null, toId: null };
    const parts = slug.split('-to-');
    if (parts.length !== 2) return { fromId: null, toId: null };
    return { fromId: parts[0], toId: parts[1] };
  };

  const { fromId, toId } = parseConversion(conversion);
  const fromUnit = getUnitById(fromId);
  const toUnit = getUnitById(toId);

  useEffect(() => {
    if (fromUnit && toUnit && fromValue) {
      const numValue = parseFloat(fromValue);
      if (!isNaN(numValue)) {
        const result = convertPower(numValue, fromUnit.id, toUnit.id);
        if (result !== null) {
          if (Math.abs(result) < 0.000001 || Math.abs(result) > 1000000000) {
            setToValue(result.toExponential(6));
          } else {
            setToValue(parseFloat(result.toPrecision(10)).toString());
          }
        }
      }
    }
  }, [fromValue, fromUnit, toUnit]);

  const handleSwap = () => {
    if (fromUnit && toUnit) {
      window.location.href = `/convert/power/${toUnit.id}-to-${fromUnit.id}`;
    }
  };

  const relatedConversions = powerUnits.filter((u) => u.id !== fromId && u.id !== toId).slice(0, 6).map((u) => ({
    from: fromId, to: u.id, fromPlural: fromUnit?.plural || '', toPlural: u.plural, toId: u.id,
  }));

  const otherPopularConversions = popularPowerConversions.filter((c) => !(c.from === fromId && c.to === toId)).slice(0, 8);

  if (!fromUnit || !toUnit) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <LocalizedLink to="/convert/power" className="flex items-center gap-2 text-[#2563eb] hover:text-[#1d4ed8] mb-4">
          <ArrowLeft size={18} /> {t('common.backToConverter', { category: t('sidebar.converters.power') })}
        </LocalizedLink>
        <p className="text-gray-600">{t('common.invalidConversion')}</p>
      </div>
    );
  }

  const pageTitle = t('common.convertXToY', { from: t(`powerUnits.${fromUnit.id}.name`, fromUnit.name), to: t(`powerUnits.${toUnit.id}.name`, toUnit.name) });
  const pageDescription = generateSEODescription(fromUnit, toUnit);
  const conversionFactor = fromUnit.factor / toUnit.factor;
  const formulaText = t('common.formulaEquation', {
    fromName: t(`powerUnits.${fromUnit.id}.name`, fromUnit.name),
    value: conversionFactor.toExponential(6),
    toPlural: t(`powerUnits.${toUnit.id}.plural`, toUnit.plural)
  });

  return (
    <div className="space-y-4">
      <SEOHead title={t('seo.convertTitle', { from: t(`powerUnits.${fromUnit.id}.plural`, fromUnit.plural), to: t(`powerUnits.${toUnit.id}.plural`, toUnit.plural) })} description={t('seo.convertDescription', { from: t(`powerUnits.${fromUnit.id}.name`, fromUnit.name), to: t(`powerUnits.${toUnit.id}.name`, toUnit.name) })} />
      <LocalizedLink to="/convert/power" className="flex items-center gap-2 text-[#2563eb] hover:text-[#1d4ed8]">
        <ArrowLeft size={18} /> {t('common.backToConverter', { category: t('sidebar.converters.power') })}
      </LocalizedLink>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-[#4a7c9b] to-[#5a8caa] px-4 py-3">
          <h1 className="text-lg font-semibold text-white">{pageTitle}</h1>
        </div>
        <div className="p-6">
          <div className="grid md:grid-cols-[1fr,auto,1fr] gap-6 items-end">
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">{t(`powerUnits.${fromUnit.id}.plural`, fromUnit.plural)} ({fromUnit.symbol})</label>
              <input type="number" value={fromValue} onChange={(e) => setFromValue(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4a7c9b] focus:border-transparent text-lg" placeholder={t('common.enterValue')} />
            </div>
            <div className="flex justify-center">
              <button onClick={handleSwap} className="p-3 rounded-full bg-[#4a7c9b] text-white hover:bg-[#3d6a86] transition-colors shadow-md" title={t('common.swap')}>
                <ArrowLeftRight size={24} />
              </button>
            </div>
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">{t(`powerUnits.${toUnit.id}.plural`, toUnit.plural)} ({toUnit.symbol})</label>
              <input type="text" value={toValue} readOnly className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 text-lg" placeholder={t('common.result')} />
            </div>
          </div>
          {fromValue && toValue && (
            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-center text-lg text-gray-700">
                <span className="font-semibold">{fromValue}</span> <span className="text-gray-500">{t(`powerUnits.${fromUnit.id}.plural`, fromUnit.plural)}</span> = <span className="font-semibold text-[#4a7c9b] text-xl">{toValue}</span> <span className="text-gray-500">{t(`powerUnits.${toUnit.id}.plural`, toUnit.plural)}</span>
              </p>
            </div>
          )}
        </div>
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <h2 className="text-sm font-semibold text-gray-700 mb-2">{t('common.formula')}</h2>
          <p className="text-sm text-gray-600">{formulaText}</p>
          <p className="text-sm text-gray-500 mt-1">{t('common.formulaDescription', { from: t(`powerUnits.${fromUnit.id}.plural`, fromUnit.plural).toLowerCase(), to: t(`powerUnits.${toUnit.id}.plural`, toUnit.plural).toLowerCase(), factor: parseFloat(conversionFactor.toPrecision(6)) })}</p>
        </div>
        <div className="border-t border-gray-200 p-4">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">{t('common.quickReference')}</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-50"><th className="px-3 py-2 text-left font-medium text-gray-600">{t(`powerUnits.${fromUnit.id}.plural`, fromUnit.plural)} ({fromUnit.symbol})</th><th className="px-3 py-2 text-left font-medium text-gray-600">{t(`powerUnits.${toUnit.id}.plural`, toUnit.plural)} ({toUnit.symbol})</th></tr></thead>
              <tbody>
                {[1, 2, 5, 10, 20, 50, 100, 500, 1000].map((val) => {
                  const result = convertPower(val, fromUnit.id, toUnit.id);
                  const formattedResult = Math.abs(result) < 0.000001 || Math.abs(result) > 1000000000 ? result.toExponential(4) : parseFloat(result.toPrecision(8)).toString();
                  return (<tr key={val} className="border-t border-gray-100"><td className="px-3 py-2">{val} {fromUnit.symbol}</td><td className="px-3 py-2 text-[#4a7c9b] font-medium">{formattedResult} {toUnit.symbol}</td></tr>);
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      
      {/* About This Conversion Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="font-semibold text-gray-800">{t('common.unitDescriptionTitle')}</h2>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-1">{t('common.fromUnitLabel', { unit: t(`powerUnits.${fromUnit.id}.name`, fromUnit.name) })}</h3>
            <p className="text-sm text-gray-600">{t(`powerUnits.${fromUnit.id}.description`, t('common.noDescription'))}</p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-1">{t('common.toUnitLabel', { unit: t(`powerUnits.${toUnit.id}.name`, toUnit.name) })}</h3>
            <p className="text-sm text-gray-600">{t(`powerUnits.${toUnit.id}.description`, t('common.noDescription'))}</p>
          </div>
          <p className="text-sm text-gray-500 italic">{t('common.conversionNote', { from: t(`powerUnits.${fromUnit.id}.plural`, fromUnit.plural), to: t(`powerUnits.${toUnit.id}.plural`, toUnit.plural) })}</p>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50"><h2 className="font-semibold text-gray-800">{t('common.relatedConversions', { unit: t(`powerUnits.${fromUnit.id}.name`, fromUnit.name) })}</h2></div>
        <div className="p-4"><div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {relatedConversions.map((conv, idx) => (<LocalizedLink key={idx} to={`/convert/power/${conv.from}-to-${conv.to}`} className="text-sm text-gray-600 hover:text-[#2563eb] transition-colors py-1">{t('common.xToY', { from: t(`powerUnits.${conv.from}.plural`, conv.fromPlural), to: t(`powerUnits.${conv.toId}.plural`, conv.toPlural) })}</LocalizedLink>))}
        </div></div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50"><h2 className="font-semibold text-gray-800">{t('common.otherPopularConversions', { category: t('sidebar.converters.power') })}</h2></div>
        <div className="p-4"><div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {otherPopularConversions.map((conv, idx) => {
            const from = getUnitById(conv.from); const to = getUnitById(conv.to);
            if (!from || !to) return null;
            return (<LocalizedLink key={idx} to={`/convert/power/${conv.from}-to-${conv.to}`} className="text-sm text-gray-600 hover:text-[#2563eb] transition-colors py-1">{t('common.xToY', { from: t(`powerUnits.${from.id}.plural`, from.plural), to: t(`powerUnits.${to.id}.plural`, to.plural) })}</LocalizedLink>);
          })}
        </div></div>
      </div>
    </div>
  );
};

export default PowerConversionPage;
